import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Sidenav from "../components/Sidenav";
import CreateTagAssignment from "../components/CreateTagAssignment";
import EditTagAssignment from "../components/EditTagAssignment";
import DeleteTagAssignmentModal from "../components/DeleteTagAssignmentModal";
import RequestBleSTQuote from "../components/RequestBleSTQuote";
import "../Console.css";

const MyBleST = ({ domain, userData }) => {
  const navigate = useNavigate();
  const [showCreateTagAssignmentModal, setShowCreateTagAssignmentModal] =
    useState(false);
  const [showEditTagAssignmentModal, setShowEditTagAssignmentModal] =
    useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [editAssignment, setEditAssignment] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedOrderAssignmentId, setSelectedOrderAssignmentId] =
    useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showDeleteAssignmentModal, setShowDeleteAssignmentModal] =
    useState(false);
  const [deleteAssignment, setDeleteAssignment] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const searchInputRef = useRef(null);
  const [orgLogoMap, setOrgLogoMap] = useState({});
  const [userOrgName, setUserOrgName] = useState("");

  const styles = (
    <style>{`
      /* Search bar */
      .search-wrap {
        position: sticky; top: 0; z-index: 10;
        background: #f3f4f6; padding: 12px 20px 10px 20px;
      }
      .search-bar {
        display: flex; align-items: center; gap: 10px;
        background: #fff; border: 1.5px solid #e5e7eb; border-radius: 9999px;
        padding: 10px 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        transition: border-color 0.15s, box-shadow 0.15s;
      }
      .search-bar.focused { border-color: #ff5100; box-shadow: 0 0 0 3px rgba(255,81,0,0.1); }
      .search-icon { color: #9ca3af; font-size: 14px; flex-shrink: 0; transition: color 0.15s; }
      .search-bar.focused .search-icon { color: #ff5100; }
      .search-input { flex: 1; border: none; outline: none; background: transparent; font-size: 14px; color: #111827; }
      .search-input::placeholder { color: #9ca3af; }
      .search-shortcut {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 11px; font-weight: 700;
        background: #f3f4f6; border: 1px solid #e5e7eb; border-bottom-width: 2px;
        border-radius: 5px; padding: 1px 6px; color: #6b7280;
        user-select: none; flex-shrink: 0;
      }
      .search-clear {
        border: 1px solid #e5e7eb; background: #f3f4f6; color: #374151;
        padding: 3px 10px; border-radius: 9999px; font-size: 12px; font-weight: 700;
        flex-shrink: 0; cursor: pointer;
      }
      .search-clear:hover { background: #e5e7eb; }

      /* Order-assignment cards */
      .order-card {
        background: #fff; border-radius: 18px; border: 1px solid #f3f4f6;
        box-shadow: 0 1px 4px rgba(0,0,0,0.05); overflow: hidden;
        margin-bottom: 16px; transition: box-shadow 0.15s;
      }
      .order-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
      .order-card-header {
        display: flex; align-items: center; gap: 14px;
        padding: 16px 20px; border-bottom: 1px solid #f9fafb; flex-wrap: wrap;
      }
      .order-card-icon {
        width: 40px; height: 40px; border-radius: 11px; flex-shrink: 0;
        background: rgba(255,81,0,0.08); border: 1.5px solid rgba(255,81,0,0.2);
        display: flex; align-items: center; justify-content: center;
      }
      .order-card-title { font-size: 15px; font-weight: 800; color: #111827; line-height: 1.2; }
      .order-card-sub { font-size: 12px; color: #9ca3af; margin-top: 2px; }
      .order-pill {
        display: inline-flex; align-items: center; gap: 5px;
        padding: 4px 10px; border-radius: 9999px;
        font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
      }
      .order-pill-orange {
        background: rgba(255,81,0,0.08); border: 1.5px solid rgba(255,81,0,0.2); color: #c13d00;
      }
      .order-pill-grey {
        background: #f3f4f6; border: 1.5px solid #e5e7eb; color: #374151;
      }

      /* Metadata grid */
      .order-meta-grid {
        display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 0;
      }
      .order-meta-cell {
        padding: 14px 20px; border-right: 1px solid #f3f4f6; border-bottom: 1px solid #f3f4f6;
      }
      .order-meta-cell:last-child { border-right: none; }
      .order-meta-label {
        font-size: 10.5px; font-weight: 700; color: #9ca3af;
        text-transform: uppercase; letter-spacing: 0.5px;
        display: flex; align-items: center; gap: 5px; margin-bottom: 5px;
      }
      .order-meta-label i { font-size: 10px; width: 11px; text-align: center; }
      .order-meta-value {
        font-size: 13.5px; color: #111827; font-weight: 600;
        word-break: break-word; overflow-wrap: anywhere;
      }

      /* Card footer / actions */
      .order-card-actions {
        display: flex; align-items: center; gap: 10px;
        padding: 14px 20px; flex-wrap: wrap;
      }
      .btn-action {
        display: inline-flex; align-items: center; gap: 7px;
        padding: 8px 18px; border-radius: 9999px;
        font-size: 13px; font-weight: 700; cursor: pointer;
        border: 1.5px solid; transition: background 0.15s, box-shadow 0.15s;
        text-decoration: none;
      }
      .btn-action:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
      .btn-action-toggle { background: #f9fafb; border-color: #e5e7eb; color: #374151; }
      .btn-action-toggle:hover { background: #f3f4f6; }
      .btn-action-toggle.active {
        background: rgba(255,81,0,0.07); border-color: rgba(255,81,0,0.3); color: #c13d00;
      }
      .btn-action-assign {
        background: rgba(5,150,105,0.07); border-color: rgba(5,150,105,0.3); color: #065f46;
      }
      .btn-action-assign:hover { background: rgba(5,150,105,0.12); }
      .btn-action-invoice {
        background: rgba(255,81,0,0.07); border-color: rgba(255,81,0,0.25); color: #c13d00;
      }
      .btn-action-invoice:hover { background: rgba(255,81,0,0.12); }
      .btn-action-ml { margin-left: auto; }

      /* Tag assignments panel */
      .assignments-panel {
        border-top: 1px solid #f3f4f6; background: #fafafa; padding: 16px 20px;
      }
      .assignments-table {
        width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px;
      }
      .assignments-table thead th {
        font-size: 10.5px; font-weight: 700; color: #9ca3af;
        text-transform: uppercase; letter-spacing: 0.5px;
        padding: 0 12px 10px; border-bottom: 1.5px solid #e5e7eb; white-space: nowrap;
      }
      .assignments-table thead th:first-child { padding-left: 0; }
      .assignments-table tbody tr:not(:last-child) td { border-bottom: 1px solid #f3f4f6; }
      .assignments-table tbody td { padding: 11px 12px; color: #374151; vertical-align: top; }
      .assignments-table tbody td:first-child { padding-left: 0; }
      .serial-mono {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 12px; background: #f3f4f6; border-radius: 6px;
        padding: 2px 7px; color: #374151; display: inline-block;
      }
      .item-code {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 12px; color: #374151;
      }
      .meta-kv { font-size: 12px; line-height: 1.6; }
      .meta-kv-key { font-weight: 700; color: #6b7280; }
      .assignments-empty {
        padding: 24px 0; text-align: center; color: #9ca3af; font-size: 13px;
      }
      .assignments-empty i { font-size: 20px; margin-bottom: 8px; display: block; }

      /* Actions menu (ellipsis) */
      .card-actions { position: relative; display: inline-block; }
      .icon-ellipsis {
        display: inline-flex; align-items: center; justify-content: center;
        width: 32px; height: 32px; border-radius: 8px;
        background: #fff; color: #111827; border: 1px solid #e5e7eb; font-size: 13px;
        cursor: pointer;
      }
      .icon-ellipsis:hover { background: #f9fafb; }
      .menu-pop {
        position: absolute; bottom: calc(100% + 4px); top: auto; right: 0;
        background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.08);
        min-width: 160px; padding: 6px; z-index: 50;
      }
      .menu-item {
        width: 100%; display: flex; align-items: center; gap: 10px;
        border: none; background: transparent; text-align: left;
        padding: 8px 10px; border-radius: 8px; font-size: 13px; color: #111827; font-weight: 700;
        cursor: pointer;
      }
      .menu-item i { font-size: 13px; }
      .menu-item:hover { background: #f3f4f6; }
      .menu-item.danger { color: #b91c1c; }
      .menu-item.danger:hover { background: #fff1f2; }
    `}</style>
  );

  // Close actions menu on outside click or Esc
  useEffect(() => {
    const onDocClick = () => setOpenMenuId(null);
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenMenuId(null);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // "/" shortcut focuses search
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "/") return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (
        showCreateTagAssignmentModal ||
        showEditTagAssignmentModal ||
        showDeleteAssignmentModal
      )
        return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [
    showCreateTagAssignmentModal,
    showEditTagAssignmentModal,
    showDeleteAssignmentModal,
  ]);

  const userType = useMemo(
    () => (userData?.[1] || "").toString().toLowerCase().trim(),
    [userData],
  );
  const clientIdFromUser = useMemo(
    () => (userData?.[5] || "").toString().trim(),
    [userData],
  );
  const isBrandAdmin = userType === "brandadmin";

  const handleCloseCreateTagAssignmentModal = () =>
    setShowCreateTagAssignmentModal(false);
  const handleShowCreateTagAssignmentModal = () =>
    setShowCreateTagAssignmentModal(true);

  const toggleExpanded = (index) =>
    setExpandedIndex(expandedIndex === index ? null : index);

  const handleCreateAssignment = (orderIndex) => {
    const order = orders[orderIndex];
    if (!order || !order._id) {
      alert("Invalid order selected.");
      return;
    }
    setSelectedOrderAssignmentId(order._id);
    setShowCreateTagAssignmentModal(true);
  };

  const handleDeleteAssignment = (assignment) => {
    setDeleteAssignment(assignment);
    setShowDeleteAssignmentModal(true);
    setOpenMenuId(null);
  };

  const handleEditAssignment = (assignment, order) => {
    setEditOrder(order);
    setEditAssignment(assignment);
    setShowEditTagAssignmentModal(true);
    setOpenMenuId(null);
  };

  const fetchOrderAssignments = useCallback(async () => {
    try {
      setIsLoading(true);
      const url =
        isBrandAdmin && clientIdFromUser
          ? `https://api.iriscrypt.com/orderAssignment/by-client/${encodeURIComponent(clientIdFromUser)}`
          : "https://api.iriscrypt.com/orderAssignment";
      const [response, orgsRes] = await Promise.all([
        fetch(url, {
          headers: { "x-api-key": "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43" },
        }),
        fetch("/api/organizations"),
      ]);
      const data = await response.json();
      const orgs = await orgsRes.json();
      const logoMap = {};
      (orgs || []).forEach((o) => {
        if (o.organization && (o.logoUrl || o.imageUrl)) {
          logoMap[o.organization] = o.logoUrl || o.imageUrl;
        }
      });
      setOrgLogoMap(logoMap);
      const currentOrg = (orgs || []).find((o) => o._id === clientIdFromUser);
      setUserOrgName(currentOrg?.organization || "");
      const formatted = (data || []).map((a) => ({
        _id: a._id,
        date: (a.date || "").split("T")[0],
        quantity: a.maxSerial - a.minSerial + 1,
        serialRange: `${a.minSerial} - ${a.maxSerial}`,
        tagType: `${a.type} (${a.length}mm)`,
        clientName: a.client?.name || "",
        tagAssignments: a.tagAssignments || [],
      }));
      setOrders(formatted);
    } catch (error) {
      console.error("Error fetching order assignments:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isBrandAdmin, clientIdFromUser]);

  useEffect(() => {
    const userReady = Array.isArray(userData) && userData.length >= 6;
    if (!userReady) return;
    if (isBrandAdmin && !clientIdFromUser) return;
    fetchOrderAssignments();
  }, [userData, isBrandAdmin, clientIdFromUser, fetchOrderAssignments]);

  const handleFormSubmit = () => {
    fetchOrderAssignments();
  };

  const modalData = orders.find(
    (order) => order._id === selectedOrderAssignmentId,
  );

  const filteredOrders = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return orders;
    const matches = (str) =>
      typeof str === "string" && str.toLowerCase().includes(q);
    return orders.filter((order) => {
      const baseMatch =
        matches(order.date) ||
        matches(order.serialRange) ||
        matches(order.tagType);
      if (baseMatch) return true;
      return order.tagAssignments.some((a) => {
        const metaText = Object.entries(a.metadata || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join(" ");
        return (
          matches(`${a.minSerial} - ${a.maxSerial}`) ||
          matches(a.itemCode || "") ||
          matches(metaText) ||
          matches((a.date || "").split("T")[0])
        );
      });
    });
  }, [orders, searchTerm]);

  return (
    <>
      <Helmet>
        <title>Security Tags | BLEEP</title>
        <meta
          name="description"
          content="Introducing BLEEP, the revolutionary crowdsourcing app designed to combat counterfeit products and services."
        />
      </Helmet>

      {styles}

      <div className="d-flex">
        <Sidenav domain={domain} />
        <div
          style={{
            flex: 1,
            position: "relative",
            height: "calc(100vh - 94px)",
            overflowY: "auto",
          }}
        >
          {/* Header — preserved as-is */}
          <header
            style={{
              position: "relative",
              paddingInline: "30px",
              paddingBlock: "30px",
              color: "#fff",
              background:
                "linear-gradient(135deg, rgba(255,81,0,1) 0%, rgba(255,0,0,1) 100%)",
              overflow: "hidden",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: "url('/images/background-art.jpg')",
                backgroundRepeat: "repeat",
                backgroundSize: "400px 400px",
                backgroundPosition: "center",
                mixBlendMode: "overlay",
                opacity: 0.1,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              className="row d-flex align-items-center"
              style={{ position: "relative", zIndex: 1 }}
            >
              <div
                className="col"
                style={{ padding: 10 }}
              >
                <div className="col d-flex align-items-center">
                  <b
                    style={{
                      fontSize: "30px",
                      textShadow: "#1c1c1c 1px 2px 0px",
                    }}
                  >
                    <i className="fas fa-sticky-note"></i> BLEEP Security Tags
                    (BleST)
                  </b>
                </div>
                <p
                  style={{
                    paddingTop: "10px",
                    textShadow: "#1c1c1c 1px 1px 0px",
                  }}
                >
                  Protect your products and services from counterfeiting with
                  BleST.
                </p>
                <div
                  style={{
                    alignItems: "center",
                    display: "flex",
                    gap: "10px",
                    marginTop: "15px",
                  }}
                >
                  {userOrgName === "Greenbridge" && (
                    <button
                      type="button"
                      className="btn btn-light btn-lg"
                      style={{
                        borderRadius: "30px",
                        boxShadow: "rgba(0, 0, 0, 0.25) 0px 3px 8px",
                        paddingBlock: "10px",
                        paddingInline: "30px",
                      }}
                      onClick={() => setShowQuoteModal(true)}
                    >
                      <span style={{ marginRight: "10px" }}>
                        <i className="fa-solid fa-circle-plus"></i>
                      </span>
                      <b>Order Tags</b>
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-light btn-lg"
                    style={{
                      borderRadius: "30px",
                      boxShadow: "rgba(0, 0, 0, 0.25) 0px 3px 8px",
                      paddingBlock: "10px",
                      paddingInline: "30px",
                    }}
                    onClick={() => navigate("/bleststatementofaccounts")}
                  >
                    <span style={{ marginRight: "10px" }}>
                      <i className="fa-solid fa-file-invoice"></i>
                    </span>
                    <b>Statement of Accounts</b>
                  </button>
                </div>
              </div>
            </div>
          </header>

          {/* Search */}
          <div style={{ backgroundColor: "#f3f4f6" }}>
            <div className="search-wrap">
              <div
                className={`search-bar${searchFocused ? " focused" : ""}`}
                role="search"
              >
                <i
                  className="fas fa-search search-icon"
                  aria-hidden="true"
                />
                <input
                  ref={searchInputRef}
                  className="search-input"
                  type="search"
                  placeholder="Search your tags…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  aria-label="Search your tags"
                />
                {!searchTerm && (
                  <kbd
                    className="search-shortcut"
                    title="Press / to focus"
                  >
                    /
                  </kbd>
                )}
                {searchTerm && (
                  <button
                    className="search-clear"
                    onClick={() => {
                      setSearchTerm("");
                      searchInputRef.current?.focus();
                    }}
                    title="Clear search"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          <div style={{ padding: "20px", backgroundColor: "#f3f4f6" }}>
            {/* Tip banner */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                background: "#fff7f3",
                border: "1.5px solid rgba(255,81,0,0.2)",
                borderRadius: 14,
                padding: "16px 18px",
                marginBottom: 20,
                boxShadow: "0 1px 4px rgba(255,81,0,0.06)",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  flexShrink: 0,
                  background: "rgba(255,81,0,0.1)",
                  border: "1.5px solid rgba(255,81,0,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="fa-solid fa-lightbulb"
                  style={{ color: "#ff5100", fontSize: 15 }}
                />
              </div>
              <div
                style={{ lineHeight: 1.65, fontSize: 13.5, color: "#374151" }}
              >
                <span
                  style={{ fontWeight: 800, color: "#c13d00", fontSize: 13 }}
                >
                  Tip
                </span>
                <span style={{ color: "#9ca3af", margin: "0 6px" }}>·</span>
                Tags assigned to your account will appear below. You can further
                segment and assign these serial ranges to link them with
                specific{" "}
                <a
                  href="/productskus"
                  style={{
                    color: "#ff5100",
                    fontWeight: 700,
                    textDecoration: "none",
                  }}
                >
                  Product SKUs
                </a>
                .
              </div>
            </div>

            {isLoading ? (
              <div
                style={{
                  minHeight: "240px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#6b7280",
                  gap: 12,
                }}
              >
                <i
                  className="fas fa-spinner fa-spin"
                  style={{ fontSize: "28px" }}
                  aria-hidden="true"
                />
                <div style={{ fontSize: 13 }}>Loading your tags...</div>
              </div>
            ) : (
              <>
                {filteredOrders.map((order, index) => {
                  const isExpanded = expandedIndex === index;
                  return (
                    <div
                      key={index}
                      className="order-card"
                    >
                      {/* Card header */}
                      <div className="order-card-header">
                        <div className="order-card-icon">
                          <i
                            className="fas fa-tag"
                            style={{ color: "#ff5100", fontSize: 15 }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="order-card-title">
                            {order.quantity?.toLocaleString()} tags
                          </div>
                          <div className="order-card-sub">{order.date}</div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          {order.clientName &&
                            (() => {
                              const logo = orgLogoMap[order.clientName];
                              return (
                                <span
                                  className="order-pill order-pill-grey"
                                  style={{ gap: 6 }}
                                >
                                  {logo ? (
                                    <img
                                      src={logo}
                                      alt={order.clientName}
                                      style={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        flexShrink: 0,
                                        border: "1px solid rgba(0,0,0,0.08)",
                                      }}
                                    />
                                  ) : (
                                    <i
                                      className="fas fa-building"
                                      style={{ fontSize: 9 }}
                                    />
                                  )}
                                  {order.clientName}
                                </span>
                              );
                            })()}
                        </div>
                      </div>

                      {/* Metadata grid */}
                      {(() => {
                        const assignedQty = order.tagAssignments.reduce(
                          (sum, a) => sum + (a.maxSerial - a.minSerial + 1),
                          0,
                        );
                        const unassigned = order.quantity - assignedQty;
                        return (
                          <div className="order-meta-grid">
                            <div className="order-meta-cell">
                              <div className="order-meta-label">
                                <i className="fas fa-barcode" /> Serial Range
                              </div>
                              <div
                                className="order-meta-value"
                                style={{
                                  fontFamily:
                                    "ui-monospace,SFMono-Regular,Menlo,monospace",
                                  fontSize: 12.5,
                                }}
                              >
                                {order.serialRange}
                              </div>
                            </div>
                            <div className="order-meta-cell">
                              <div className="order-meta-label">
                                <i className="fas fa-tag" /> Unassigned Tags
                              </div>
                              <div
                                className="order-meta-value"
                                style={{
                                  color:
                                    unassigned === 0
                                      ? "#059669"
                                      : unassigned < 0
                                        ? "#dc2626"
                                        : "#111827",
                                }}
                              >
                                {unassigned.toLocaleString()}
                              </div>
                            </div>
                            <div className="order-meta-cell">
                              <div className="order-meta-label">
                                <i className="fas fa-expand-arrows-alt" /> Size
                              </div>
                              <div className="order-meta-value">
                                {order.tagType}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Actions */}
                      <div className="order-card-actions">
                        <button
                          className={`btn-action btn-action-toggle${isExpanded ? " active" : ""}`}
                          onClick={() => toggleExpanded(index)}
                        >
                          <i
                            className={`fas ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"}`}
                            style={{ fontSize: 11 }}
                          />
                          {isExpanded
                            ? "Hide Assignments"
                            : `Tag Assignments (${order.tagAssignments.length})`}
                        </button>
                        <button
                          className="btn-action btn-action-assign"
                          onClick={() => handleCreateAssignment(index)}
                        >
                          <i
                            className="fas fa-plus"
                            style={{ fontSize: 11 }}
                          />
                          Assign
                        </button>
                        <button
                          className="btn-action btn-action-invoice btn-action-ml"
                          onClick={() => {
                            navigate(`/blestinvoice?assignment=${order._id}`);
                          }}
                        >
                          <i
                            className="fas fa-file-invoice-dollar"
                            style={{ fontSize: 11 }}
                          />
                          View Invoice
                        </button>
                      </div>

                      {/* Tag assignments panel */}
                      {isExpanded && (
                        <div className="assignments-panel">
                          {order.tagAssignments.length === 0 ? (
                            <div className="assignments-empty">
                              <i className="fas fa-inbox" />
                              No tag assignments yet — click{" "}
                              <strong>Assign</strong> above to create one.
                            </div>
                          ) : (
                            <table className="assignments-table">
                              <thead>
                                <tr>
                                  <th>Serial Range</th>
                                  <th>Qty</th>
                                  <th>Product SKU</th>
                                  <th>Metadata</th>
                                  <th>Date</th>
                                  <th style={{ textAlign: "right" }}>
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {order.tagAssignments.map((a, i) => {
                                  const menuOpen = openMenuId === a._id;
                                  const qty = a.maxSerial - a.minSerial + 1;
                                  return (
                                    <tr key={i}>
                                      <td>
                                        <span className="serial-mono">
                                          {a.minSerial} – {a.maxSerial}
                                        </span>
                                      </td>
                                      <td
                                        style={{
                                          color: "#374151",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {qty.toLocaleString()}
                                      </td>
                                      <td>
                                        <span className="item-code">
                                          {a.itemCode || "—"}
                                        </span>
                                      </td>
                                      <td>
                                        {Object.entries(a.metadata || {})
                                          .length === 0 ? (
                                          <span style={{ color: "#9ca3af" }}>
                                            —
                                          </span>
                                        ) : (
                                          <div className="meta-kv">
                                            {Object.entries(
                                              a.metadata || {},
                                            ).map(([k, v]) => (
                                              <div key={k}>
                                                <span className="meta-kv-key">
                                                  {k}:
                                                </span>{" "}
                                                <span
                                                  style={{ color: "#374151" }}
                                                >
                                                  {v}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </td>
                                      <td
                                        style={{
                                          whiteSpace: "nowrap",
                                          color: "#6b7280",
                                        }}
                                      >
                                        {(a.date || "").split("T")[0]}
                                      </td>
                                      <td style={{ textAlign: "right" }}>
                                        <div
                                          className="card-actions"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <button
                                            className="icon-ellipsis"
                                            aria-haspopup="menu"
                                            aria-expanded={menuOpen}
                                            title="Actions"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenMenuId(
                                                menuOpen ? null : a._id,
                                              );
                                            }}
                                          >
                                            <i className="fas fa-ellipsis-h" />
                                          </button>
                                          {menuOpen && (
                                            <div
                                              className="menu-pop"
                                              role="menu"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            >
                                              <button
                                                className="menu-item"
                                                role="menuitem"
                                                onClick={() =>
                                                  handleEditAssignment(a, order)
                                                }
                                              >
                                                <i className="fas fa-pen-to-square" />
                                                Edit
                                              </button>
                                              <button
                                                className="menu-item danger"
                                                role="menuitem"
                                                onClick={() =>
                                                  handleDeleteAssignment(a)
                                                }
                                              >
                                                <i className="fas fa-trash-alt" />
                                                Delete
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {orders.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      color: "#9ca3af",
                      marginTop: 48,
                      fontSize: 14,
                    }}
                  >
                    <i
                      className="fas fa-tag"
                      style={{
                        fontSize: 28,
                        marginBottom: 10,
                        display: "block",
                      }}
                    />
                    No tag assignments yet.
                  </div>
                ) : (
                  filteredOrders.length === 0 && (
                    <div
                      style={{
                        textAlign: "center",
                        color: "#9ca3af",
                        marginTop: 48,
                        fontSize: 14,
                      }}
                    >
                      <i
                        className="fas fa-search"
                        style={{
                          fontSize: 28,
                          marginBottom: 10,
                          display: "block",
                        }}
                      />
                      No tag assignments match "{searchTerm}".
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <CreateTagAssignment
        domain={domain}
        show={showCreateTagAssignmentModal}
        handleClose={handleCloseCreateTagAssignmentModal}
        handleFormSubmit={handleFormSubmit}
        orderAssignmentId={selectedOrderAssignmentId}
        isBrandAdmin={isBrandAdmin}
        clientId={clientIdFromUser}
        modalData={orders.find((o) => o._id === selectedOrderAssignmentId)}
      />
      <EditTagAssignment
        show={showEditTagAssignmentModal}
        handleClose={() => setShowEditTagAssignmentModal(false)}
        handleFormSubmit={handleFormSubmit}
        orderAssignmentId={editOrder?._id}
        orderData={editOrder}
        assignment={editAssignment}
        isBrandAdmin={isBrandAdmin}
        clientId={clientIdFromUser}
      />
      <DeleteTagAssignmentModal
        show={showDeleteAssignmentModal}
        handleClose={() => setShowDeleteAssignmentModal(false)}
        assignmentId={deleteAssignment?._id}
        serialRange={
          deleteAssignment
            ? `${deleteAssignment.minSerial} - ${deleteAssignment.maxSerial}`
            : ""
        }
        onDeleted={fetchOrderAssignments}
      />
      <RequestBleSTQuote
        show={showQuoteModal}
        handleClose={() => setShowQuoteModal(false)}
        userData={userData}
      />
    </>
  );
};

export default MyBleST;
